import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createWebHistory } from 'vue-router'
import App from './App.vue'
import { vuetify } from './plugins/vuetify'
import { createAppRouter } from './router'
import { createAppI18n } from './i18n'
import { boot } from './boot'
import { provideKeycloak, startKeycloak } from './plugins/keycloak'
import { KeycloakHttpClient } from './api/http'
import { connectApi, provideApi } from './api/jsonapi'
import { useAuth } from './stores/auth'
import { usePrefs } from './stores/prefs'
import { useSemesters } from './stores/semesters'
import * as Sentry from '@sentry/vue'

// Sentry starts before anything can fail, so a failed login, API connection or first load is reported too.
// Its Vue integration needs the app, which exists only after those, so it is added then. The release is the
// one the Sentry build plugin wrote into the bundle: the commit CI built (SENTRY_RELEASE), as for the API.
Sentry.init({
    dsn: 'https://76feaa4742bd06e04322e1c1001277db@o4512260184604672.ingest.de.sentry.io/4512352858079312',
    environment: import.meta.env.MODE,
    debug: false,
    integrations: (defaults) => [...defaults.filter((integration) => integration.name !== 'Vue'), Sentry.browserTracingIntegration()],
    // The same rate as the API's traces_sample_rate; the API follows the browser's decision for the requests it traces.
    tracesSampleRate: 0.1,
    // Only the API gets the sentry-trace and baggage headers, so its transactions join the browser's trace.
    tracePropagationTargets: [/^\/api\//, `${window.location.origin}/api/`],
    // Sentry is a third party: query strings carry usernames and the teacher search terms (?username=,
    // contains(fullName,…)), and request bodies carry lessons and teachers, so neither is collected.
    dataCollection: { urlQueryParams: false, httpBodies: [] },
})

const root = document.getElementById('app') as HTMLElement

void boot({
    root,
    report: (error) => Sentry.captureException(error),
    start: async () => {
        const keycloak = await startKeycloak()
        provideKeycloak(keycloak)
        provideApi(await connectApi(new KeycloakHttpClient(keycloak)))
        const pinia = createPinia()
        await Promise.all([useAuth(pinia).load(), useSemesters(pinia).load()])
        const app = createApp(App)
        Sentry.addIntegration(Sentry.vueIntegration({ app }))
        app.use(pinia)
            .use(vuetify)
            .use(createAppI18n(usePrefs(pinia).locale))
            .use(createAppRouter(createWebHistory()))
            .mount(root)
    },
})
